import React from 'react';
import PropTypes from 'prop-types';
import { Avatar } from '../../atoms/Avatar';
import SupportAvatar from '../SupportAvatar';
import { useUserAvatar } from '../../../hooks/useUserAvatar';

export const ResolvedUserAvatar = ({
    alt = 'User avatar',
    className,
    imgClassName,
    id,
    avatarUrl,
    shouldUseSupportAvatar = false,
    size,
    radius,
    fallbackSrc,
    imgProps,
    style,
    defaultAvatarBackground = false,
}) => {
    const fallback = shouldUseSupportAvatar ? (
        <SupportAvatar
            ariaLabel={alt}
            size={null}
        />
    ) : null;

    return (
        <Avatar
            src={shouldUseSupportAvatar ? null : avatarUrl}
            alt={alt}
            className={className}
            imgClassName={imgClassName}
            size={size}
            radius={radius}
            fallbackSrc={fallbackSrc}
            fallback={fallback}
            imgProps={imgProps}
            style={style}
            id={id}
            defaultAvatarBackground={defaultAvatarBackground}
        />
    );
};

export const UserAvatar = ({
    userId,
    username,
    alt = 'User avatar',
    className,
    imgClassName,
    id,
    avatarUrl: overrideAvatarUrl,
    size,
    radius,
    fallbackSrc,
    imgProps,
    style,
    audience = 'viewer',
    defaultAvatarBackground = false,
}) => {
    const hasOverrideAvatarUrl = typeof overrideAvatarUrl === 'string' && overrideAvatarUrl.trim() !== '';
    const { avatarUrl, rawUrl, shouldUseSupportAvatar } = useUserAvatar(userId, {
        username,
        audience,
        enabled: !hasOverrideAvatarUrl,
    });

    const effectiveAvatar = hasOverrideAvatarUrl ? overrideAvatarUrl : avatarUrl;
    const shouldUseDefaultAvatarBackground = defaultAvatarBackground
        && !hasOverrideAvatarUrl
        && !rawUrl
        && !shouldUseSupportAvatar;

    return (
        <ResolvedUserAvatar
            avatarUrl={effectiveAvatar}
            shouldUseSupportAvatar={hasOverrideAvatarUrl ? false : shouldUseSupportAvatar}
            alt={alt}
            className={className}
            imgClassName={imgClassName}
            size={size}
            radius={radius}
            fallbackSrc={fallbackSrc}
            imgProps={imgProps}
            style={style}
            id={id}
            defaultAvatarBackground={shouldUseDefaultAvatarBackground}
        />
    );
};

UserAvatar.propTypes = {
    userId: PropTypes.string,
    username: PropTypes.string,
    alt: PropTypes.string,
    className: PropTypes.string,
    id: PropTypes.string,
    avatarUrl: PropTypes.string,
    imgClassName: PropTypes.string,
    size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    radius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    fallbackSrc: PropTypes.string,
    imgProps: PropTypes.object,
    style: PropTypes.object,
    audience: PropTypes.oneOf(['viewer', 'public']),
    defaultAvatarBackground: PropTypes.bool,
};

ResolvedUserAvatar.propTypes = {
    alt: PropTypes.string,
    className: PropTypes.string,
    id: PropTypes.string,
    avatarUrl: PropTypes.string,
    imgClassName: PropTypes.string,
    shouldUseSupportAvatar: PropTypes.bool,
    size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    radius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    fallbackSrc: PropTypes.string,
    imgProps: PropTypes.object,
    style: PropTypes.object,
    defaultAvatarBackground: PropTypes.bool,
};

export default UserAvatar;
